import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { ChartFragment, Section } from 'models/api-response';
import { FormFieldInputType } from 'models/enum';
import { ChartActionsComponentProps, ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { chartHelper } from 'helpers';
import { chartService } from 'services';
import { appSelectors } from 'redux/ducks/app';
import { studentSelectors } from 'redux/ducks/student';
import { withChartLogic } from 'components/common';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import { DropdownAction, FormField, FormFieldLabel, SectionTitle } from './constants';
import NeurologicalEvaluationView, { NeurologicalEvaluationViewProps } from './NeurologicalEvaluationView';
import { Title } from '../shared/constants';


const DROPDOWN_FIELDS: Array<{ name: FormField; label: FormFieldLabel }> = [
  { name: FormField.LEFT_PUPIL_SIZE, label: FormFieldLabel.LEFT_PUPIL_SIZE },
  { name: FormField.RIGHT_PUPIL_SIZE, label: FormFieldLabel.RIGHT_PUPIL_SIZE },
  { name: FormField.LEFT_PUPIL_REACTION, label: FormFieldLabel.LEFT_PUPIL_REACTION },
  { name: FormField.RIGHT_PUPIL_REACTION, label: FormFieldLabel.RIGHT_PUPIL_REACTION },
  { name: FormField.ARM_MOVEMENT, label: FormFieldLabel.ARM_MOVEMENT },
  { name: FormField.LEG_MOVEMENT, label: FormFieldLabel.LEG_MOVEMENT },
  { name: FormField.COMA_STATUS, label: FormFieldLabel.COMA_STATUS }
];

const buildPupilShapeRecord = (formFieldMap: Map<string, ChartMetaFormField>) => {
  const isLeftRound = !!formFieldMap.get(FormField.LEFT_PUPIL)?.chartContent?.[0]?.selected;
  const isRightRound = !!formFieldMap.get(FormField.RIGHT_PUPIL)?.chartContent?.[0]?.selected;
  if (!isLeftRound && !isRightRound) return null;
  return chartService.buildPlainRecord({
    formField: 'pupilShape',
    title: 'Pupil Shape',
    content: DropdownAction.ROUND_LABEL,
    value: 'round',
    contentId: ''
  });
};

const buildProviderNurseRecord = (isAuthor: boolean, authorName: string, student: { firstName?: string; lastName?: string } | undefined) => {
  const resolvedName = isAuthor ? (authorName || '').trim() : `${student?.firstName ?? ''} ${student?.lastName ?? ''}`.trim();
  if (!resolvedName) return null;
  return chartService.buildPlainRecord({
    formField: FormField.PROVIDER_NURSE,
    title: FormFieldLabel.PROVIDER_NURSE,
    content: resolvedName,
    value: resolvedName,
    contentId: ''
  });
};

export interface NeurologicalEvaluationProps extends ChartComponentProps {
  userRole?: string;
  authorName?: string;
}

class NeurologicalEvaluation extends Component<NeurologicalEvaluationProps> {
  static displayName = 'NeurologicalEvaluation';

  componentDidMount() {
    this.props.initState(this.buildDefaultFormFields());
  }

  buildDefaultFormFields = (): Map<string, ChartMetaFormField> => {
    const { createFormField } = chartHelper;
    const dataMap = new Map<string, ChartMetaFormField>();

    dataMap.set(FormField.LEFT_PUPIL, createFormField({ name: FormField.LEFT_PUPIL, label: FormFieldLabel.LEFT_PUPIL, type: FormFieldInputType.CHECK_BOX }));
    dataMap.set(FormField.RIGHT_PUPIL, createFormField({ name: FormField.RIGHT_PUPIL, label: FormFieldLabel.RIGHT_PUPIL, type: FormFieldInputType.CHECK_BOX }));

    DROPDOWN_FIELDS.forEach(({ name, label }) => {
      const field = createFormField({ name, label, type: FormFieldInputType.DROPDOWN });
      dataMap.set(name, field);
    });

    return dataMap;
  };

  createSections = (): Section[] => {
    const { formFieldMap, isAuthor, authorName, student } = this.props;
    const { buildPatientRecord } = chartService;
    return [
      {
        sectionTitle: SectionTitle.GENERAL_INFO,
        records: [
          buildProviderNurseRecord(isAuthor, authorName, student)
        ]
      },
      {
        sectionTitle: SectionTitle.PUPILS,
        records: [buildPupilShapeRecord(formFieldMap), buildPatientRecord(formFieldMap, FormField.LEFT_PUPIL_SIZE), buildPatientRecord(formFieldMap, FormField.RIGHT_PUPIL_SIZE)]
      },
      {
        sectionTitle: SectionTitle.PUPIL_REACTION,
        records: [buildPatientRecord(formFieldMap, FormField.LEFT_PUPIL_REACTION), buildPatientRecord(formFieldMap, FormField.RIGHT_PUPIL_REACTION)]
      },
      {
        sectionTitle: SectionTitle.LIMB_MOVEMENT,
        records: [buildPatientRecord(formFieldMap, FormField.ARM_MOVEMENT), buildPatientRecord(formFieldMap, FormField.LEG_MOVEMENT)]
      },
      {
        sectionTitle: SectionTitle.COMA_STATUS,
        records: [buildPatientRecord(formFieldMap, FormField.COMA_STATUS)]
      }
    ];
  };

  buildFragment = (): ChartFragment => {
    const record = {
      chartTitle: Title.SPECIAL_CHARTS,
      fragmentTitle: SectionTitle.NEUROLOGICAL_EVALUATION,
      records: this.createSections()
    };
    const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
    const basicInfo = chartService.createBaseFragment({ chartingTime: this.props.chartingTime });
    return { ...basicInfo, chartData: cleanRecord } as ChartFragment;
  };

  handleSaveClick = () => {
    this.props.saveChart([this.buildFragment()], {
      defaultFormFields: this.buildDefaultFormFields(),
      afterSave: () => {
        if (!this.props.isAuthor) {
          this.props.navigateToSavedPatientCharting();
        }
      }
    });
  };

  handleCancelClick = () => this.props.handleDiscardClick(undefined, this.buildDefaultFormFields());

  render() {
    const { displayAuthoringData, enableDisplayRecordsButton, formFieldMap, formSubmittedCount, hasUnsavedChanges, isAuthor, userRole } = this.props;

    const chartActionsProps: ChartActionsComponentProps = {
      enableDisplayRecordsButton,
      enableSaveButton: hasUnsavedChanges,
      onCancelClick: this.handleCancelClick,
      onDisplayRecordsClick: displayAuthoringData,
      onSaveClick: this.handleSaveClick
    };

    const viewProps: NeurologicalEvaluationViewProps = {
      chartActionsProps,
      formFieldMap,
      formSubmittedCount,
      isAuthor,
      userRole
    };
    return <NeurologicalEvaluationView {...viewProps} />;
  }
}

export { NeurologicalEvaluation as BaseNeurologicalEvaluation };

const mapStateToProps = (state) => ({
  userRole: appSelectors.getUserRole(state),
  authorName: studentSelectors.getAuthorName(state)
});

export default compose(connect(mapStateToProps), withSavedPatientChartsPage, withChartLogic)(NeurologicalEvaluation);
